import { keepDisplayedTimestamp } from "@/lib/utils";
import type {
  ResearchChatData,
  ResearchChatMessage,
  SendMessageResponse,
} from "@/types/research.types";

export const RESEARCH_POLL_MS = 5000;

const TERMINAL_RESEARCH_STATUSES = new Set([
  "completed",
  "complete",
  "failed",
  "error",
  "timeout",
  "timed_out",
  "cancelled",
  "canceled",
]);

function textOrEmpty(value: string | null | undefined): string {
  return value ?? "";
}

export function isPendingResearchTurn(message: ResearchChatMessage | undefined): boolean {
  if (message == null) return false;
  const status = message.status;
  if (status === "pending") return true;
  if (status != null && TERMINAL_RESEARCH_STATUSES.has(status)) return false;
  if (status?.startsWith("http_error_") === true) return false;
  const hasQuestion = textOrEmpty(message.question).trim() !== "";
  const hasResponse = textOrEmpty(message.response).trim() !== "";
  return hasQuestion && !hasResponse;
}

export function isDeferredResearchResponse(data: SendMessageResponse): boolean {
  if (data.status === "pending") return true;
  const hasResponse = data.response != null && data.response.trim() !== "";
  const hasQueryId = data.query_id != null && data.query_id !== "";
  return !hasResponse && hasQueryId;
}

export function researchChatRefetchInterval(data: ResearchChatData | undefined): number | false {
  const turns = data?.prev_chat ?? [];
  const last = turns[turns.length - 1];
  return isPendingResearchTurn(last) ? RESEARCH_POLL_MS : false;
}

export function applyResearchReply(
  oldData: ResearchChatData | undefined,
  question: string,
  data: SendMessageResponse
): ResearchChatData | undefined {
  if (oldData == null) return oldData;
  const lastIndex = oldData.prev_chat.length - 1;
  return {
    ...oldData,
    prev_chat: oldData.prev_chat.map((message, index) => {
      if (index !== lastIndex || message.question !== question) return message;
      if (isDeferredResearchResponse(data)) {
        return {
          ...message,
          status: "pending",
          query_id: data.query_id ?? message.query_id,
        };
      }
      return {
        ...message,
        response: data.response ?? message.response,
        related_questions: data.related_questions ?? message.related_questions,
        creation_date: keepDisplayedTimestamp(message.creation_date, data.creation_date),
        status: "completed",
      };
    }),
  };
}

export function mergeResearchChat(
  cached: ResearchChatData | undefined,
  incoming: ResearchChatData
): ResearchChatData {
  const cachedTurns = cached?.prev_chat ?? [];
  const incomingTurns = incoming.prev_chat ?? [];
  const usedCached = new Set<number>();
  const merged = incomingTurns.map((incomingTurn) => {
    const cachedIndex = cachedTurns.findIndex((turn, index) => {
      if (usedCached.has(index)) return false;
      const incomingQueryId = incomingTurn.query_id;
      if (incomingQueryId != null && incomingQueryId !== "" && turn.query_id === incomingQueryId) {
        return true;
      }
      return turn.question === incomingTurn.question;
    });
    if (cachedIndex < 0) return incomingTurn;
    usedCached.add(cachedIndex);
    const cachedTurn = cachedTurns[cachedIndex];
    if (cachedTurn == null) return incomingTurn;
    return {
      ...incomingTurn,
      creation_date: keepDisplayedTimestamp(cachedTurn.creation_date, incomingTurn.creation_date),
    };
  });
  const leftoverPending = cachedTurns.filter(
    (turn, index) => !usedCached.has(index) && isPendingResearchTurn(turn)
  );
  return { ...incoming, prev_chat: [...merged, ...leftoverPending] };
}
